import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Sparkles } from "lucide-react";
import { SITE, telLink, whatsappLink, mailLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { SERVICE_AREAS } from "@/data/content";
import { RESIDENTIAL_CARDS, COMMERCIAL_CARDS } from "@/data/services";

const FOOTER_SERVICES = [...RESIDENTIAL_CARDS.slice(0, 4), ...COMMERCIAL_CARDS.slice(0, 3)];

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-slate-950 text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="font-display text-4xl font-black leading-tight text-white sm:text-5xl">
            Let's get it <span className="text-emerald-400">clean.</span>
          </h2>
          <p className="mt-4 text-slate-400">
            Professional end-of-tenancy and move-out cleaning across {SITE.serviceArea}. Send photos
            for a fast, clear quote.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-500">
              Get a Free Quote
            </Link>
            <a
              href={whatsappLink()} onClick={() => track.whatsappClick("footer")}
              target="_blank" rel="noreferrer" data-testid="footer-whatsapp"
              className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-8 border-t border-white/10 pt-12 md:grid-cols-4">
          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white"><Sparkles size={16} /></span>
              <span className="font-display font-extrabold text-white">GoCleaning</span>
            </div>
            <p className="text-sm text-slate-400">
              Local property cleaning service focused on clear communication, reliable appointments
              and straightforward pricing.
            </p>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-emerald-400">Services</h3>
            <ul className="space-y-2 text-sm">
              {FOOTER_SERVICES.map((s) => (
                <li key={s.name + s.href}><Link to={s.href} className="text-slate-400 transition-colors hover:text-white">{s.name}</Link></li>
              ))}
              <li><Link to="/moving-cleaning-packages" className="text-slate-400 transition-colors hover:text-white">Moving &amp; Cleaning Packages</Link></li>
              <li><Link to="/landlords-estate-agents" className="text-slate-400 transition-colors hover:text-white">Landlords &amp; Estate Agents</Link></li>
              <li><Link to="/services" className="font-semibold text-emerald-400 hover:text-white">All services →</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-emerald-400">Service Areas</h3>
            <ul className="space-y-2 text-sm">
              {[...SERVICE_AREAS.Dublin.slice(0, 4), ...SERVICE_AREAS.Kildare].map((a) => (
                <li key={a}><a href="#service-areas" className="text-slate-400 transition-colors hover:text-white">{a}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-emerald-400">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={telLink()} onClick={track.phoneClick} data-testid="footer-phone" className="flex items-center gap-2 text-slate-300 hover:text-white">
                  <Phone size={15} /> {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={whatsappLink()} onClick={() => track.whatsappClick("footer-list")} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-white">
                  <MessageCircle size={15} /> WhatsApp
                </a>
              </li>
              <li>
                <a href={mailLink()} onClick={track.emailClick} data-testid="footer-email" className="flex items-center gap-2 text-slate-300 hover:text-white">
                  <Mail size={15} /> {SITE.email}
                </a>
              </li>
              <li className="pt-2 text-slate-400">{SITE.hours.weekdays}</li>
              <li className="text-slate-400">{SITE.hours.sunday}</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <p className="text-sm text-slate-400">
            Need removals or furniture transport?{" "}
            <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" data-testid="footer-govans-link" className="font-bold text-emerald-400 hover:text-white">
              Visit GoVans.ie
            </a>
          </p>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-slate-500 md:flex-row">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-white" data-testid="footer-cookie">Cookie Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white" data-testid="footer-terms">Terms & Conditions</Link>
            <a href="/sitemap.xml" className="hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
